import React, { useEffect, useState } from 'react';
import { useFormik } from 'formik';
import { Table } from 'reactstrap';
import * as Yup from 'yup';
import {
  Box,
  Button,
  Checkbox,
  FormControl,
  FormLabel,
  Input,
  Stack,
  Text,
  Textarea,
  SimpleGrid,
  useDisclosure,
  Modal,
  ModalOverlay,
  ModalContent,
  ModalHeader,
  ModalBody,
  ModalFooter,
  ModalCloseButton,
  Badge,
  Flex,
  HStack,
  Tooltip,
} from '@chakra-ui/react';
import { toast } from 'react-toastify';
import httpInjectorService from 'services/http-injector.service';
import { useSelector } from 'react-redux';
import { FaEye, FaUndo } from 'react-icons/fa';

const OffBoarding = () => {
  const { isOpen, onOpen, onClose } = useDisclosure();
  const [selectedItem, setSelectedItem] = useState(null);
  const [resignations, setResignations] = useState([]);
  const [showTable, setShowTable] = useState(false);
  const [isUndoOpen, setUndoOpen] = useState(false);
  const [undoItem, setUndoItem] = useState(null);

  const profiledata = useSelector((state) => state.Authentication?.profiledata);
  const currentUserId = profiledata?.[0]?.user_id;

  // AFTER — picks the latest resignation (correct, matches DESC order from API)
  const latestResignation = resignations[0];

  const handleView = (item) => {
    setSelectedItem(item);
    onOpen();
  };

  const handleUndoClick = (item) => {
    setUndoItem(item);
    setUndoOpen(true);
  };

  // updated
  const handleConfirmUndo = async () => {
    try {
      const payload = {
        id: undoItem.id, // ✅ targets only this specific row
      };
      const response = await httpInjectorService.undoResignation(payload);

      if (response.status === 'success') {
        toast.success('Resignation undone');

        setResignations((prev) =>
          prev.map(
            (res) =>
              res.id === undoItem.id ? { ...res, status: 'undo' } : res, // ✅ only updates this specific row in UI
          ),
        );

        setUndoOpen(false);
      } else {
        toast.error(response.message);
      }
    } catch (err) {
      toast.error('Undo failed');
    }
  };

  const formik = useFormik({
    initialValues: {
      mobileNumber: '',
      email: '',
      emergencyContactNumber: '',
      address: '',
      reason: '',
      declaration: false,
    },
    validationSchema: Yup.object({
      mobileNumber: Yup.string()
        .required('Mobile number is required')
        .matches(/^[0-9]{10}$/, 'Enter a valid 10-digit mobile number'),

      email: Yup.string()
        .email('Enter a valid email')
        .required('Email is required'),

      emergencyContactNumber: Yup.string()
        .required('Emergency contact is required')
        .matches(/^[0-9]{10}$/, 'Enter a valid 10-digit number'),

      address: Yup.string()
        .required('Address is required')
        .min(5, 'Address is too short'),

      reason: Yup.string()
        .required('Please provide a reason for resignation')
        .min(5, 'Reason should be meaningful'),

      declaration: Yup.boolean().oneOf(
        [true],
        'You must confirm before submitting',
      ),
    }),
    onSubmit: async (values) => {
      onOpen(); // ✅ open modal ONLY after validation passes
    },
  });

  // FETCH RESIGNATION DATA api
  const fetchResignations = async () => {
    try {
      const response = await httpInjectorService.getResignedUsers();

      if (response.status === 'success') {
        const filtered = response.data.filter(
          (item) => item.user_id === profiledata?.[0]?.user_id,
        );

        setResignations(filtered);

        if (filtered.length > 0) {
          setShowTable(true);
        }
      }
    } catch (err) {
      console.error(err);
    }
  };

  useEffect(() => {
    if (currentUserId) {
      fetchResignations();
    }
  }, [currentUserId]);

  const handleConfirmation = async () => {
    try {
      const response = await httpInjectorService.resignation(formik.values);

      if (response.status === 'success') {
        toast.success(response.message);

        await fetchResignations();
        setShowTable(true);
        formik.resetForm();
        onClose();
      } else {
        toast.error(response.message);
      }
    } catch (error) {
      toast.error('Something went wrong');
    }
  };

  return (
    <Box maxH="70vh" overflowY="auto" maxWidth="100vw">
      {!showTable ? (
        <>
          <Text fontSize="xl" mb="4" fontWeight="bold">
            Resignation
          </Text>

          <form onSubmit={formik.handleSubmit}>
            <Stack spacing="6">
              <SimpleGrid columns={2} spacing={6}>
                <FormControl>
                  <FormLabel>Name</FormLabel>
                  <Input value={profiledata?.[0]?.username} disabled />
                </FormControl>

                <FormControl>
                  <FormLabel>Employee ID</FormLabel>
                  <Input value={profiledata?.[0]?.employee_id} disabled />
                </FormControl>

                <FormControl
                  isInvalid={
                    formik.touched.mobileNumber && formik.errors.mobileNumber
                  }
                >
                  <FormLabel>Mobile</FormLabel>

                  <Input
                    name="mobileNumber"
                    value={formik.values.mobileNumber}
                    onChange={formik.handleChange}
                    onBlur={formik.handleBlur}
                  />

                  {formik.touched.mobileNumber &&
                    formik.errors.mobileNumber && (
                      <Text color="red.500" fontSize="sm">
                        {formik.errors.mobileNumber}
                      </Text>
                    )}
                </FormControl>

                <FormControl
                  isInvalid={formik.touched.email && formik.errors.email}
                >
                  <FormLabel>Email</FormLabel>

                  <Input
                    name="email"
                    value={formik.values.email}
                    onChange={formik.handleChange}
                    onBlur={formik.handleBlur}
                  />

                  {formik.touched.email && formik.errors.email && (
                    <Text color="red.500" fontSize="sm">
                      {formik.errors.email}
                    </Text>
                  )}
                </FormControl>

                <FormControl
                  isInvalid={
                    formik.touched.emergencyContactNumber &&
                    formik.errors.emergencyContactNumber
                  }
                >
                  <FormLabel>Emergency Contact</FormLabel>
                  <Input
                    name="emergencyContactNumber"
                    value={formik.values.emergencyContactNumber}
                    onChange={formik.handleChange}
                    onBlur={formik.handleBlur}
                  />
                  {formik.touched.emergencyContactNumber &&
                    formik.errors.emergencyContactNumber && (
                      <Text color="red.500" fontSize="sm">
                        {formik.errors.emergencyContactNumber}
                      </Text>
                    )}
                </FormControl>

                <FormControl
                  isInvalid={formik.touched.address && formik.errors.address}
                >
                  <FormLabel>Address</FormLabel>
                  <Textarea
                    name="address"
                    value={formik.values.address}
                    onChange={formik.handleChange}
                    onBlur={formik.handleBlur}
                  />

                  {formik.touched.address && formik.errors.address && (
                    <Text color="red.500" fontSize="sm">
                      {formik.errors.address}
                    </Text>
                  )}
                </FormControl>
              </SimpleGrid>

              <FormControl
                isInvalid={formik.touched.reason && formik.errors.reason}
              >
                <FormLabel>Reason</FormLabel>

                <Textarea
                  name="reason"
                  value={formik.values.reason}
                  onChange={formik.handleChange}
                  onBlur={formik.handleBlur}
                />

                {formik.touched.reason && formik.errors.reason && (
                  <Text color="red.500" fontSize="sm">
                    {formik.errors.reason}
                  </Text>
                )}
              </FormControl>

              <Checkbox
                name="declaration"
                isChecked={formik.values.declaration}
                onChange={formik.handleChange}
              >
                I understand and agree to submit my resignation request
              </Checkbox>

              {formik.touched.declaration && formik.errors.declaration && (
                <Text color="red.500" fontSize="sm">
                  {formik.errors.declaration}
                </Text>
              )}

              <Button
                type="submit"
                colorScheme="purple"
                isDisabled={!formik.values.declaration}
              >
                Apply
              </Button>
            </Stack>
          </form>

          {/* ✅ MODAL */}
          <Modal isOpen={isOpen} onClose={onClose}>
            <ModalOverlay />
            <ModalContent>
              <ModalHeader>Confirm</ModalHeader>
              <ModalCloseButton />
              <ModalBody>Submit resignation?</ModalBody>
              <ModalFooter>
                <Button colorScheme="purple" onClick={handleConfirmation}>
                  Yes
                </Button>
                <Button onClick={onClose}>No</Button>
              </ModalFooter>
            </ModalContent>
          </Modal>
        </>
      ) : (
        <Box style={{ padding: '0px', maxWidth: '100vw' }} overflowX="auto">
          <Flex justify="space-between" align="center" mb={4}>
            <Text fontSize="lg" fontWeight="bold" mb={0}>
              Resignation
            </Text>

            <Flex justify="flex-end" flex={1}>
              {(latestResignation?.status === 'rejected' ||
                latestResignation?.status === 'undo' ||
                resignations.length === 0) && (
                <Button
                  onClick={() => setShowTable(false)}
                  colorScheme="purple"
                >
                  Apply Resignation
                </Button>
              )}
            </Flex>
          </Flex>

          <Table bordered>
            <thead
              style={{
                textAlign: 'center',
                fontWeight: 'bold',
                color: 'black',
                position: 'sticky',
                verticalAlign: 'middle',
                top: 0,
                zIndex: 1,
              }}
            >
              <tr>
                <th style={{ backgroundColor: 'rgb(181, 142, 228)' }}>
                  Mobile
                </th>
                <th style={{ backgroundColor: 'rgb(181, 142, 228)' }}>Email</th>
                <th style={{ backgroundColor: 'rgb(181, 142, 228)' }}>
                  Address
                </th>
                <th style={{ backgroundColor: 'rgb(181, 142, 228)' }}>
                  Status
                </th>
                <th style={{ backgroundColor: 'rgb(181, 142, 228)' }}>Date</th>
                <th style={{ backgroundColor: 'rgb(181, 142, 228)' }}>
                  Last working Day
                </th>
                <th style={{ backgroundColor: 'rgb(181, 142, 228)' }}>
                  Actions
                </th>
                {/* <th style={{ backgroundColor: 'rgb(181, 142, 228)' }}>
                  Reason
                </th> */}
              </tr>
            </thead>

            <tbody>
              {resignations.length > 0 ? (
                resignations.map((item, i) => (
                  <tr key={i}>
                    <td>{item.mobile_number}</td>
                    <td>{item.personalemail_id}</td>
                    <td>{item.address}</td>
                    <td>
                      <Badge
                        colorScheme="green"
                        px={2}
                        py={1}
                        borderRadius="md"
                        style={{
                          color:
                            item.status === 'approved'
                              ? 'green'
                              : item.status === 'rejected'
                                ? 'red'
                                : 'orange',
                          fontWeight: 'bold',
                        }}
                      >
                        {item.status ? item.status : 'pending'}
                      </Badge>
                    </td>
                    <td>
                      {new Date(
                        item.resignation_timestamp,
                      ).toLocaleDateString()}
                    </td>
                    <td>
                      {item.Last_working_day
                        ? new Date(item.Last_working_day).toLocaleDateString()
                        : '-'}
                    </td>
                    <td>
                      <Flex gap={2} justify="center">
                        <Tooltip label="View Resignation" hasArrow>
                          <Button
                            size="xs"
                            borderRadius="sm"
                            colorScheme="blue"
                            onClick={() => handleView(item)}
                          >
                            <FaEye />
                          </Button>
                        </Tooltip>

                        {/* Only allow undo if still pending — once HR acts, it's final */}
                        {(item.status === null ||
                          item.status === 'pending' ||
                          item.status === '') && (
                          <Tooltip label="Withdraw Resignation" hasArrow>
                            <Button
                              size="xs"
                              borderRadius="sm"
                              colorScheme="orange"
                              onClick={() => handleUndoClick(item)}
                            >
                              <FaUndo />
                            </Button>
                          </Tooltip>
                        )}

                        {/* Show a clear message if HR has already acted */}
                        {(item.status === 'approved' ||
                          item.status === 'rejected') && (
                          <Tooltip
                            label="HR has already processed this resignation"
                            hasArrow
                          >
                            <Button
                              borderRadius="sm"
                              size="xs"
                              colorScheme="gray"
                              isDisabled
                            >
                              <FaUndo />
                            </Button>
                          </Tooltip>
                        )}
                      </Flex>
                    </td>
                  </tr>
                ))
              ) : (
                <tr>
                  <td colSpan="6" align="center">
                    No Data Found
                  </td>
                </tr>
              )}
            </tbody>
          </Table>

          {/* view modal */}
          <Modal isOpen={isOpen} onClose={onClose} size="lg">
            <ModalOverlay />
            <ModalContent>
              <ModalHeader>Resignation Details</ModalHeader>
              <ModalCloseButton />
              <ModalBody>
                <Text>
                  <b>Name:</b> {selectedItem?.Name}
                </Text>
                <Text>
                  <b>Employee ID:</b> {selectedItem?.Employee_id}
                </Text>
                <Text>
                  <b>Email:</b> {selectedItem?.personalemail_id}
                </Text>
                <Text>
                  <b>Mobile:</b> {selectedItem?.mobile_number}
                </Text>
                <Text>
                  <b>Address:</b> {selectedItem?.address}
                </Text>
                <Text>
                  <b>Reason:</b>{' '}
                  {selectedItem?.reason_for_resignation
                    ? selectedItem.reason_for_resignation
                    : '-'}
                </Text>

                <Text mt={2}>
                  <b>Status:</b> {selectedItem?.status || 'Pending'}
                </Text>

                <Text>
                  <b>Applied On:</b>{' '}
                  {selectedItem?.resignation_timestamp
                    ? new Date(
                        selectedItem.resignation_timestamp,
                      ).toLocaleString()
                    : '-'}
                </Text>

                <Text>
                  <b>Last Working Day:</b>{' '}
                  {selectedItem?.Last_working_day
                    ? new Date(
                        selectedItem.Last_working_day,
                      ).toLocaleDateString()
                    : '-'}
                </Text>
              </ModalBody>

              <ModalFooter>
                <Button onClick={onClose}>Close</Button>
              </ModalFooter>
            </ModalContent>
          </Modal>

          {/* confirm delete modal */}
          <Modal isOpen={isUndoOpen} onClose={() => setUndoOpen(false)}>
            <ModalOverlay />
            <ModalContent>
              <ModalHeader>Undo Resignation</ModalHeader>
              <ModalCloseButton />
              <ModalBody>
                Are you sure you want to take back your resignation request?
              </ModalBody>
              <ModalFooter>
                <Button colorScheme="orange" onClick={handleConfirmUndo}>
                  Yes, Undo
                </Button>
                <Button onClick={() => setUndoOpen(false)}>Cancel</Button>
              </ModalFooter>
            </ModalContent>
          </Modal>
        </Box>
      )}
    </Box>
  );
};

export default OffBoarding;
